import { useCallback } from 'react';
import { isPointInAnyPath, addStartPointToPathEnd } from '../utils';
import { createPathGeoJson } from '../../../Services/DataServices/DataIOService';
import { clearSessionPaths } from '../../../Services/UtilityServices';
import { manhattanDistance } from '../../../Map/utils';

// Vehicle path actions hook
export const useVehiclePathActions = ({
  vehiclePaths,
  setVehiclePaths,
  selectedVehicleIndex,
  setSelectedVehicleIndex,
  startId,
  locations,
  setMapPointSelect,
  activeCallbackIndexRef,
  isHandlingMapPointRef,
  lastSelectedPointRef,
  updatePathThrottled,
  setPath,
  setPathComplete,
  findLocationById
}) => {
  // Calculate total path distance for a vehicle
  const calculateVehiclePathDistance = useCallback((vehiclePath) => {
    if (!vehiclePath || !vehiclePath.path || vehiclePath.path.length < 2) {
      return 0;
    }
    
    let totalDistance = 0;
    
    for (let i = 0; i < vehiclePath.path.length - 1; i++) {
      const current = vehiclePath.path[i];
      const next = vehiclePath.path[i + 1];
      
      if (current && next && current.latitude !== undefined && next.latitude !== undefined) {
        totalDistance += manhattanDistance(
          current.latitude, 
          current.longitude, 
          next.latitude, 
          next.longitude
        );
      }
    }
    
    return totalDistance;
  }, []);

  // Update vehicle path with calculated distance
  const updateVehiclePathWithDistance = useCallback((vehiclePath) => {
    const distance = calculateVehiclePathDistance(vehiclePath);
    return {
      ...vehiclePath,
      distance: distance
    };
  }, [calculateVehiclePathDistance]);
  // Handle map point selection function, separated from state update
  const handleMapPointSelection = useCallback((point, vehicleIndex) => {
    if (vehicleIndex === null || vehicleIndex >= vehiclePaths.length) {
      return;
    }
    
    // Check if point already exists in other vehicle paths (excluding start point)
    if (point.id !== startId && isPointInAnyPath(point.id, vehiclePaths, startId)) {
      alert(`${point.name} has already been added to the path of other vehicles. Each point can only be added to one path.`);
      return;
    }
    
    // Use functional update to ensure based on latest state
    setVehiclePaths(currentPaths => {
      // Create new path array, retain existing path points
      const newPaths = [...currentPaths];
      const currentVehicle = {...newPaths[vehicleIndex]};
      const currentPath = [...currentVehicle.path];
      
      // Check if point already exists in path of this vehicle
      const pointIndex = currentPath.findIndex(p => p && p.id === point.id);
      
      if (pointIndex >= 0) {
        // If point exists in path, truncate to that point
        currentVehicle.path = currentPath.slice(0, pointIndex + 1);
      } else {
        // Otherwise, add to path end, retain existing points
        currentVehicle.path = [...currentPath, point];
      }
      
      newPaths[vehicleIndex] = currentVehicle;
      
      // Path update immediately triggered within functional update
      setTimeout(() => {
        if (currentVehicle.path.length >= 2) {
          const pathIds = currentVehicle.path.map(p => p ? p.id : null).filter(Boolean);
          
          // Only update if path has different points
          if (new Set(pathIds).size > 1) {
            updatePathThrottled(newPaths);
          }
        }
      }, 0);
      
      return newPaths;
    });
  }, [vehiclePaths, startId, updatePathThrottled, setVehiclePaths, locations]);

  // Select vehicle path and handle map point selection callbacks
  const selectVehiclePath = useCallback((index) => {

    // First clear any existing callbacks to prevent multiple callbacks
    if (setMapPointSelect) {

      setMapPointSelect(null);
      activeCallbackIndexRef.current = null;
    }
    
    // If clicking on the currently selected path, just deselect
    if (selectedVehicleIndex === index) {

      setSelectedVehicleIndex(null);
    } else {

      setSelectedVehicleIndex(index);
      
      // Set map point selection callback to add points to the selected vehicle path
      if (setMapPointSelect) {

        // Create a stable callback function with additional check for active vehicle
        const callbackFn = (point) => {

          // Only process if this is still the active callback for the selected vehicle
          if (activeCallbackIndexRef.current !== index) {
            return;
          }
          
          // Check if we're currently handling a map point to prevent double-processing
          if (isHandlingMapPointRef.current) {
            return;
          }
          
          // Record map point selection status
          isHandlingMapPointRef.current = true;
          lastSelectedPointRef.current = {...point};
          
          // Use functional update to ensure latest state is obtained
          handleMapPointSelection(point, index);
          
          // Set a delay to reset the handling state
          setTimeout(() => {
            isHandlingMapPointRef.current = false;
          }, 500);
        };
        
        // Update active callback index reference BEFORE setting the callback
        activeCallbackIndexRef.current = index;
        
        // Save callback function reference
        setMapPointSelect(callbackFn);

      } else {
        console.warn(`[DEBUG-CUSTOM] setMapPointSelect is not available!`);
      }
    }
  }, [selectedVehicleIndex, setSelectedVehicleIndex, setMapPointSelect, activeCallbackIndexRef, 
      isHandlingMapPointRef, lastSelectedPointRef, handleMapPointSelection]);
  
  // Reset specific vehicle path
  const resetVehiclePath = useCallback((vehicleIndex) => {
    if (vehicleIndex === null || vehicleIndex >= vehiclePaths.length) {
      return;
    }
    
    // Use functional update
    setVehiclePaths(currentPaths => {
      const newPaths = [...currentPaths];
      // Only retain start point and original color
      const resetVehicle = {
        ...newPaths[vehicleIndex],
        path: startId ? [findLocationById(startId)] : []
        // Keep existing color
      };
      
      // Update vehicle with calculated distance
      newPaths[vehicleIndex] = updateVehiclePathWithDistance(resetVehicle);
      
      // Path update immediately triggered within functional update
      setTimeout(() => {
        updatePathThrottled(newPaths);
      }, 0);
      
      return newPaths;
    });
    
    // Note: Session clearing will be handled during path confirmation, not on every edit
    
    setPathComplete(false);
  }, [vehiclePaths.length, startId, findLocationById, updatePathThrottled, setVehiclePaths, setPathComplete, updateVehiclePathWithDistance]);
  
  // Reset all vehicle paths
  const resetAllPaths = useCallback(() => {

    // Create reset paths with start points and calculate distances
    const resetPaths = vehiclePaths.map(vehicle => {
      const resetVehicle = {
        ...vehicle,
        path: startId ? [findLocationById(startId)] : []
        // Keep existing colors
      };
      return updateVehiclePathWithDistance(resetVehicle);
    });
    
    // Immediately clear path lines from map
    setPath([]);
    
    // Note: Session clearing optimized - will be handled during confirmation
    
    // Immediately update path state in UI
    setVehiclePaths(resetPaths);
    
    // Delay path update to ensure state is updated
    setTimeout(() => {
      updatePathThrottled(resetPaths);
    }, 50);
    
    // Reset path completion status
    setPathComplete(false);
  }, [vehiclePaths, startId, findLocationById, setPath, updatePathThrottled, setVehiclePaths, setPathComplete, updateVehiclePathWithDistance]);
  
  // Remove point from path
  const removePointFromPath = useCallback((vehicleIndex, pointIndex) => {
    if (vehicleIndex === null || vehicleIndex >= vehiclePaths.length) {
      return;
    }
    
    // Check if point is start point before removing
    const isStartPoint = pointIndex === 0 && 
                         vehiclePaths[vehicleIndex].path[0] && 
                         vehiclePaths[vehicleIndex].path[0].id === startId;
                         
    if (isStartPoint) {
      return;
    }
    
    // Use functional update
    setVehiclePaths(currentPaths => {
      const newPaths = [...currentPaths];
      const currentVehicle = {...newPaths[vehicleIndex]};
      const currentPath = [...currentVehicle.path];
      
      // Check once to ensure pointIndex is within valid range
      if (pointIndex < 0 || pointIndex >= currentPath.length) {
        return currentPaths; // Return original path, no changes
      }
      
      // Remove specified point
      currentVehicle.path = [
        ...currentPath.slice(0, pointIndex),
        ...currentPath.slice(pointIndex + 1)
      ];
      
      // Update vehicle with calculated distance
      newPaths[vehicleIndex] = updateVehiclePathWithDistance(currentVehicle);
      
      // Path update immediately triggered within functional update
      setTimeout(() => {
        updatePathThrottled(newPaths);
      }, 0);
      
      return newPaths;
    });
    
    // Note: Session clearing will be handled during path confirmation, not on every edit
    
    setPathComplete(false);
  }, [vehiclePaths, startId, updatePathThrottled, setVehiclePaths, setPathComplete, updateVehiclePathWithDistance]);
  
  // Function to undo last path point
  const undoLastPoint = useCallback((vehicleIndex) => {
    if (vehicleIndex === null || vehicleIndex >= vehiclePaths.length) {
      return;
    }
    
    const currentVehicle = vehiclePaths[vehicleIndex];
    
    // If path does not exist or has only one point (start point) or is empty, cannot undo
    if (!currentVehicle.path || currentVehicle.path.length <= 1) {
      return;
    }
    
    // Use functional update to ensure based on latest state
    setVehiclePaths(currentPaths => {
      const newPaths = [...currentPaths];
      const updatedVehicle = {...newPaths[vehicleIndex]};
      
      // Remove last point
      updatedVehicle.path = updatedVehicle.path.slice(0, -1);
      
      // Update vehicle with calculated distance
      newPaths[vehicleIndex] = updateVehiclePathWithDistance(updatedVehicle);
      
      // Path update immediately triggered within functional update
      setTimeout(() => {
        updatePathThrottled(newPaths);
      }, 0);
      
      return newPaths;
    });
    
    // Note: Session clearing will be handled during path confirmation, not on every edit
    
    // Set path to incomplete status
    setPathComplete(false);

  }, [vehiclePaths, updatePathThrottled, setVehiclePaths, setPathComplete, updateVehiclePathWithDistance]);
  
  // Add point to vehicle path function (for clicking button to add point)
  const addPointToSelectedVehicle = useCallback((point) => {
    if (selectedVehicleIndex === null || selectedVehicleIndex >= vehiclePaths.length) {
      return;
    }
    
    // Check if point already exists in other vehicle paths (excluding start point)
    if (point.id !== startId && isPointInAnyPath(point.id, vehiclePaths, startId)) {
      alert(`${point.name} has already been added to the path of other vehicles. Each point can only be added to one path.`);
      return;
    }
    
    // Use functional update to ensure based on latest state
    setVehiclePaths(currentPaths => {
      // Create new path array
      const newPaths = [...currentPaths];
      const currentVehicle = {...newPaths[selectedVehicleIndex]};
      
      // Add point to end of path
      currentVehicle.path = [...currentVehicle.path, point];
      // Update vehicle with calculated distance
      newPaths[selectedVehicleIndex] = updateVehiclePathWithDistance(currentVehicle);
      
      // Trigger path update immediately when path has multiple points
      if (currentVehicle.path.length >= 2) {
        updatePathThrottled(newPaths);
      }
      
      return newPaths;
    });
    
    // Note: Session clearing will be handled during path confirmation, not on every edit
    
    setPathComplete(false);
  }, [selectedVehicleIndex, vehiclePaths, startId, updatePathThrottled, setVehiclePaths, setPathComplete, updateVehiclePathWithDistance]);
  
  // Confirm all paths
  const confirmAllPaths = useCallback(() => {
    try {
      // Add duplicate click protection

      const confirmingRef = { current: true };
      
      // Check if each path has at least start point
      const allPathsValid = vehiclePaths.every(vehicle => 
        vehicle.path.length > 0 && vehicle.path[0] && vehicle.path[0].id === startId
      );
      
      if (!allPathsValid) {
        alert("Please ensure all vehicle paths have at least a start point");
        return;
      }

      // Check if all location points are already assigned to paths
      const allPathPoints = new Set(
        vehiclePaths.flatMap(vehicle => 
          vehicle.path.map(point => point ? point.id : null).filter(Boolean)
        )
      );
      
      // Check if all location points are already included in paths
      const allLocationsIncluded = locations.every(loc => allPathPoints.has(loc.id));
      
      if (!allLocationsIncluded) {
        alert("Please ensure all location points are already added to paths");
        return;
      }
      
      // Add start point to path end for all paths (if not already added)

      const pathsWithEndPoint = addStartPointToPathEnd(vehiclePaths, startId, locations);
      
      // Calculate distances for all vehicle paths

      const updatedVehiclePaths = pathsWithEndPoint.map(vehicle => updateVehiclePathWithDistance(vehicle));
      
      // Get all path point ID array from updated paths
      const allPathIds = updatedVehiclePaths.flatMap(vehicle => {
        const pathIds = vehicle.path.map(point => point ? point.id : null).filter(Boolean);
        
        // Skip paths with only start point, avoid generating zero distance paths
        if (pathIds.length <= 1) {
          return [];
        }
        
        return pathIds;
      });
      
      // Separate data into independent objects to avoid reference conflicts

              const vehiclePathsCopy = JSON.parse(JSON.stringify(updatedVehiclePaths));
              const vehicleCount = vehiclePathsCopy.length;
      
      // Clear session data during confirmation (this is appropriate)
      clearSessionPaths()
        .then(() => {
          if (!confirmingRef.current) return;

          // Update view state
          setPathComplete(true);
          setVehiclePaths(updatedVehiclePaths);
          
          // Use setTimeout to ensure state update executes in different update cycles
          window.setTimeout(() => {
            if (!confirmingRef.current) return;

            try {
              // Use simplified path data and metadata, avoid circular references
              const simplifiedPathIds = [...allPathIds];
              
              // Set path
              const metadata = { 
                vehiclePaths: vehiclePathsCopy,
                customVehicleCount: vehicleCount
              };

              setPath(simplifiedPathIds, metadata);

            } catch (innerErr) {
              console.error('[DEBUG-CUSTOM] Error in delayed path update:', innerErr);
              alert("An error occurred while confirming paths. Please try resetting paths.");
            }
          }, 300);
        })
        .catch(err => {
          console.error('[DEBUG-CUSTOM] Failed to clear session:', err);
          alert("Failed to save paths. Please try again.");
        })
        .finally(() => {
          // Set a longer delay to cancel confirming status
          window.setTimeout(() => {
            confirmingRef.current = false;
          }, 1000);
        });
    } catch (err) {
      console.error('[DEBUG-CUSTOM] Error in confirmAllPaths:', err);
      alert("An unexpected error occurred. Please try resetting paths.");
    }
  }, [vehiclePaths, startId, locations, setPath, setVehiclePaths, setPathComplete, updateVehiclePathWithDistance]);
  
  return {
    selectVehiclePath,
    handleMapPointSelection,
    resetVehiclePath,
    resetAllPaths,
    removePointFromPath,
    addPointToSelectedVehicle,
    confirmAllPaths,
    undoLastPoint
  };
}; 